import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback } from 'react'

import type { RenderFunction } from 'components/uiKit/KitTypes'
import { NOOP } from 'constants/commonConstans'
import { testProps } from 'utils/test/qaData'

import * as s from './CollapseControlled.module.scss'

interface IHeaderProps {
  open: boolean
  toggle?: () => void
}

const EXIT = { height: 0, opacity: 0 }
const INITIAL = { height: 0, opacity: 0, overflow: 'visible' }

export interface IControlledCollapseProps {
  children: React.ReactNode
  header: RenderFunction<IHeaderProps>
  styleType?: 'default' | 'ghost'
  onChangeOpen?: (val: boolean) => void
  open: boolean
  name: string
  paddings?: {
    title?: string
    content?: string
    iconGap?: number
  }
  collapsible?: 'full' | 'icon'
  styles?: React.CSSProperties
}

const CollapseControlled: React.FC<IControlledCollapseProps> = ({
  name,
  header,
  children,
  styleType = 'default',
  open,
  onChangeOpen,
  paddings,
  collapsible = 'full',
  styles,
}) => {
  const toggle = useCallback(
    (val?: boolean) => {
      const newOpen = val ?? !open
      onChangeOpen?.(newOpen)
    },
    [onChangeOpen, open],
  )

  const animate = {
    transition: { type: 'tween' },
    height: open ? 'auto' : 0,
    opacity: open ? 1 : 0.5,
    overflow: 'hidden',
  }

  const handleLabelClick = useCallback(
    () => (collapsible === 'full' ? toggle() : NOOP),
    [toggle, collapsible],
  )

  return (
    <div className={cn(s.root, s[styleType])}>
      <div
        {...testProps({ el: 'collapse', name, label: header, open })}
        className={cn(s.title)}
        onClick={handleLabelClick}
        style={{ padding: paddings?.title }}
      >
        {header({ open, toggle })}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            animate={animate}
            exit={EXIT}
            initial={INITIAL}
            style={{ overflow: 'hidden' }}
          >
            <div className={s.contentWrapper} style={{ ...styles, padding: paddings?.content }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CollapseControlled
